
import { useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { MapPin, User, CircleDot } from "lucide-react";
import { Mesh, Vector3 } from "three";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface MarkerProps {
  position: [number, number, number];
  data: {
    id: string;
    name?: string;
    ip: string;
    isCurrentUser?: boolean;
    lat?: number;
    long?: number;
  };
  color?: string;
  isSelected?: boolean;
  onClick?: () => void;
  alwaysShowInfo?: boolean;
}

const UserMarker = ({ 
  position, 
  data, 
  color = "#4ade80", 
  isSelected = false,
  onClick,
  alwaysShowInfo = false
}: MarkerProps) => {
  const markerRef = useRef<Mesh>(null);
  const pulseRef = useRef<Mesh>(null);
  const lineRef = useRef<Mesh>(null);
  
  // Calculate normalized surface position for the connection line
  const surfacePosition = useRef<[number, number, number]>([0, 0, 0]);
  
  useEffect(() => {
    if (position && position.length === 3) {
      // Calculate surface point by normalizing the position vector
      const positionVector = new Vector3(position[0], position[1], position[2]);
      const normalizedVector = positionVector.clone().normalize();
      const surfaceVector = normalizedVector.multiplyScalar(positionVector.length() * 0.98);
      surfacePosition.current = [surfaceVector.x, surfaceVector.y, surfaceVector.z];
    }
  }, [position]);
  
  // Animate pulse effect
  useFrame(({ clock }) => {
    if (pulseRef.current) {
      const scale = 1 + Math.sin(clock.getElapsedTime() * 2.5) * 0.15;
      pulseRef.current.scale.set(scale, scale, scale);
    }
    
    // Slightly rotate marker for visual appeal
    if (markerRef.current && (data.isCurrentUser || isSelected)) {
      markerRef.current.rotation.y += 0.01;
    }
  });

  // Create location display text
  const getLocationText = () => {
    if (data.lat !== undefined && data.long !== undefined) {
      return `${data.lat.toFixed(2)}°, ${data.long.toFixed(2)}°`;
    }
    return "";
  };
  
  // Create display information
  const getDisplayText = () => {
    if (data.isCurrentUser) {
      return `Your location • IP: ${data.ip}`;
    } else if (data.name) {
      return data.name;
    }
    return "Unknown";
  };

  // Determine if we should show the info label
  const showInfo = alwaysShowInfo || isSelected;
  
  // Setup pointer cursor via DOM event handlers instead of using props
  useEffect(() => {
    if (onClick) {
      document.body.style.cursor = 'pointer';
      
      return () => {
        document.body.style.cursor = 'auto';
      };
    }
  }, [onClick]);
  
  return (
    <group onClick={onClick}>
      {/* Main marker */}
      <mesh position={position} ref={markerRef}>
        <sphereGeometry args={[0.1, 16, 16]} />
        <meshStandardMaterial 
          color={color} 
          emissive={color}
          emissiveIntensity={isSelected ? 3 : 2}
        />
        
        {/* Information label */}
        {showInfo && (
          <Html
            position={[0, 0.5, 0]}
            center
            distanceFactor={12}
            occlude
          >
            <div className="bg-black/80 text-white px-2 py-1 rounded text-xs whitespace-nowrap flex flex-col items-center gap-1">
              <div className="flex items-center gap-1">
                {data.isCurrentUser ? (
                  <CircleDot className="h-3 w-3 text-red-500" />
                ) : (
                  <MapPin className="h-3 w-3 text-blue-400" />
                )}
                <span>{getDisplayText()}</span>
              </div>
              {data.lat !== undefined && (
                <span className="text-xs opacity-75">{getLocationText()}</span>
              )}
            </div>
          </Html>
        )}
      </mesh>
      
      {/* Pulsing effect */}
      <mesh position={position} ref={pulseRef}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshBasicMaterial 
          color={data.isCurrentUser ? "#ea384c" : "#60a5fa"}
          transparent={true}
          opacity={isSelected ? 0.6 : 0.4}
        />
      </mesh>
      
      {/* Connection line from marker to surface */}
      <mesh position={[
        (position[0] + surfacePosition.current[0]) / 2,
        (position[1] + surfacePosition.current[1]) / 2,
        (position[2] + surfacePosition.current[2]) / 2
      ]} ref={lineRef}>
        <cylinderGeometry 
          args={[0.01, 0.01, new Vector3(
            position[0] - surfacePosition.current[0],
            position[1] - surfacePosition.current[1],
            position[2] - surfacePosition.current[2]
          ).length(), 8]} 
        />
        <meshBasicMaterial 
          color={data.isCurrentUser ? "#ea384c" : "#60a5fa"}
          transparent={true}
          opacity={isSelected ? 0.8 : 0.6}
        />
      </mesh>
    </group>
  );
};

export default UserMarker;
